import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, Award, Zap, Clock, Smartphone, Tablet, Laptop, ArrowRight } from "lucide-react";
import RepairConfigurator from "@/components/site/RepairConfigurator";

const HIGHLIGHTS = [
  { icon: Zap, title: "Express in 60 Min", desc: "Die meisten Reparaturen erledigen wir vor Ort innerhalb einer Stunde." },
  { icon: Award, title: "Hochwertige Ersatzteile", desc: "Original- oder OEM-Qualität von zertifizierten Lieferanten — je nach Modell und Verfügbarkeit." },
  { icon: Shield, title: "6 Monate Garantie", desc: "Auf alle Reparaturen geben wir 6 Monate volle Gewährleistung." },
  { icon: Clock, title: "Faire Preise", desc: "Transparente ab-Preise — keine versteckten Kosten, keine bösen Überraschungen." },
];

const DEVICES = [
  { to: "#configurator", label: "Smartphone", desc: "iPhone, Samsung, Google, Honor & mehr", icon: Smartphone, testid: "device-smartphone" },
  { to: "/reparaturen/ipad", label: "iPad", desc: "Alle Generationen — iPad Pro, Air, mini, iPad", icon: Tablet, testid: "device-ipad" },
  { to: "/reparaturen/macbook", label: "MacBook", desc: "Air, Pro, 12″ & Retina", icon: Laptop, testid: "device-macbook" },
];

export default function RepairsPage() {
  useEffect(() => {
    if (window.location.hash === "#configurator") {
      setTimeout(() => document.getElementById("configurator")?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, []);

  return (
    <div className="pt-32 pb-24 bg-luxury-soft min-h-screen" data-testid="repairs-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Reparatur Services</div>
          <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tight text-white mt-3 leading-[1.02]">
            Reparatur-Service.<br/>
            <span className="text-zinc-500">Wir bringen dein Gerät wieder in Form.</span>
          </h1>
          <p className="text-xl text-zinc-400 mt-6 leading-relaxed">
            Professioneller Reparaturservice für Smartphones, iPads und MacBooks — mit hochwertigen Ersatzteilen und transparenten Preisen.
          </p>
        </div>

        {/* Device selector */}
        <div className="mt-14 grid md:grid-cols-3 gap-4" data-testid="repair-device-selector">
          {DEVICES.map((d) => {
            const Content = (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-[#DC1928]/12 border border-[#DC1928]/25 flex items-center justify-center">
                    <d.icon className="w-6 h-6 text-[#FF4F5E]" />
                  </div>
                  <div>
                    <div className="font-display text-lg font-bold text-white">{d.label}</div>
                    <div className="text-xs text-zinc-500">{d.desc}</div>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between text-xs">
                  <span className="text-zinc-500">Preise ansehen</span>
                  <ArrowRight className="w-4 h-4 text-[#FF4F5E]" />
                </div>
              </>
            );
            return d.to.startsWith("#") ? (
              <a key={d.testid} href={d.to} data-testid={d.testid} className="p-6 rounded-3xl bg-[#141417] border border-white/5 hover:border-white/15 transition-colors group">
                {Content}
              </a>
            ) : (
              <Link key={d.testid} to={d.to} data-testid={d.testid} className="p-6 rounded-3xl bg-[#141417] border border-white/5 hover:border-white/15 transition-colors group">
                {Content}
              </Link>
            );
          })}
        </div>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {HIGHLIGHTS.map((h, i) => (
            <div key={i} className="p-6 rounded-2xl bg-[#141417] border border-white/5" data-testid={`highlight-${i}`}>
              <h.icon className="w-7 h-7 text-[#FF2D40]" />
              <h3 className="font-display font-semibold text-white mt-4">{h.title}</h3>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{h.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-20">
          <div className="text-center mb-12">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Konfigurator</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mt-3">
              In 3 Klicks zum Angebot.
            </h2>
          </div>
          <RepairConfigurator />
        </div>
      </div>
    </div>
  );
}
